import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Loader2, MapPin, ChevronRight, Search } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { listTimecardSites } from "@/lib/review.functions";

export const Route = createFileRoute("/_authenticated/review/timecards/")({
  component: TimecardsLanding,
});

function TimecardsLanding() {
  const { currentOrg } = useAuth();
  const orgId = currentOrg?.id;
  const fetchSites = useServerFn(listTimecardSites);
  const { data, isLoading } = useQuery({
    queryKey: ["review-timecard-sites", orgId],
    queryFn: () => fetchSites({ data: { orgId: orgId! } }),
    enabled: !!orgId,
  });
  const [search, setSearch] = useState("");

  if (isLoading)
    return (
      <div className="flex items-center gap-2 py-12 text-[#C0C0C0]">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading sites…
      </div>
    );
  const sites = data?.sites ?? [];
  const filteredSites = sites.filter((s) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    const text = [s.name, s.address, s.city, s.region].filter(Boolean).join(" ").toLowerCase();
    return text.includes(q);
  });
  if (filteredSites.length === 0)
    return (
      <div className="rounded-xl border border-white/10 bg-white/[0.03] px-6 py-12 text-center text-sm text-[#999999]">
        No sites available for timecard review.
      </div>
    );

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-2 rounded-xl border border-white/10 bg-white/[0.03] p-3">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[#999]" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search sites..."
            className="rounded-md border border-white/10 bg-black/40 pl-8 pr-3 py-1.5 text-sm text-white placeholder:text-[#666]"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {filteredSites.map((s) => (
          <Link
            key={s.id}
            to="/review/timecards/$siteId"
            params={{ siteId: s.id }}
            className="group flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-4 transition-all hover:border-[#CC0000]/40 hover:bg-white/[0.05]"
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#CC0000]/15 text-[#FF8A8A]">
              <MapPin className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-white">{s.name}</div>
              <div className="truncate text-xs text-[#999999]">
                {[s.address, s.city, s.region].filter(Boolean).join(", ") || "—"}
              </div>
            </div>
            <ChevronRight className="h-4 w-4 text-[#999999] group-hover:text-[#FF8A8A]" />
          </Link>
        ))}
      </div>
    </div>
  );
}
